import { IconTrash } from '@tabler/icons-react';
import { Attachment } from '@medplum/fhirtypes';
import { ReactElement, useState } from 'react';
import { ActionIcon, FileInput, Group, Tooltip } from '@mantine/core';

type IvrAudioFileInputProps = {
  label?: string;
  audioFile: Attachment | undefined;
  disabled: boolean;
  uploading: boolean;
  uploadPromptSuffix: string;
  emptyDescription: string;
  size?: 'xs' | 'sm';
  onFileChange: (file: File | null) => void;
  onRemove: () => void;
};

/**
 * The menu's audio inputs and a `play-and-hangup` rung's upload are the same control, so it
 * lives here rather than inside the editor page that happened to need it first.
 */
export function IvrAudioFileInput({
  label,
  audioFile,
  disabled,
  uploading,
  uploadPromptSuffix,
  emptyDescription,
  size = 'sm',
  onFileChange,
  onRemove,
}: IvrAudioFileInputProps): ReactElement {
  const [inputKey, setInputKey] = useState(0);
  const isDisabled = disabled || uploading;

  return (
    <Group flex={1} align="flex-end" gap="xs" wrap="nowrap">
      <FileInput
        key={inputKey}
        flex={1}
        size={size}
        accept="audio/*"
        label={label}
        disabled={isDisabled}
        onChange={(file) => void onFileChange(file)}
        placeholder={`Click to ${audioFile?.url ? 'change' : 'upload'} ${uploadPromptSuffix}`}
        description={
          audioFile?.url ? (audioFile.title ? `Current file: ${audioFile.title}` : 'Audio set') : emptyDescription
        }
      />
      {audioFile?.url && (
        <Tooltip label="Remove audio file">
          <ActionIcon
            variant="light"
            color="red"
            size={size === 'xs' ? 'md' : 'lg'}
            disabled={isDisabled}
            onClick={() => {
              onRemove();
              setInputKey((key) => key + 1);
            }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}
